import React, { type ReactNode, useCallback, useMemo, useRef } from 'react';

import {
  Pagination,
  type PaginationProps,
  Table,
  type TableColumnType,
  type TableColumnsType,
  type TableProps,
} from 'antd-v5';
import cn from 'classnames';

import styles from './GroupedTable.module.scss';
import {
  type GroupDef,
  type GroupHeaderRow,
  type GroupPaging,
  type GroupedSummary,
  PAGE_SIZE_OPTIONS,
  type TableRow,
  isGroupHeaderRow,
  rowKeyOf,
} from './groupedTableModel';
import { type GroupingConfig, useGroupedTable } from './useGroupedTable';

type PaginationConfig = Omit<
  PaginationProps,
  'current' | 'defaultCurrent' | 'pageSize' | 'total' | 'showTotal'
>;

export interface GroupedTableProps<
  TData extends object,
  TGroup extends GroupDef,
> extends Pick<
    TableProps<TData>,
    | 'className'
    | 'size'
    | 'loading'
    | 'sticky'
    | 'scroll'
    | 'bordered'
    | 'locale'
    | 'showSorterTooltip'
    | 'tableLayout'
    | 'getPopupContainer'
  > {
  dataSource: readonly TData[];
  /** Uncontrolled antd columns (`sorter`, `onFilter`, `default*`); keep them stable */
  columns: TableColumnsType<TData>;
  /** A field of the row or a function, as antd's */
  rowKey: (keyof TData & string) | ((row: TData) => string);
  /** Flat without it; keep it stable (`useMemo`) */
  grouping?: GroupingConfig<TData, TGroup>;
  /** `whole-groups` (default) or `people`; flat tables page rows */
  groupPaging?: GroupPaging;
  /** Applied with `globalFilterFn`; debounce it in the caller */
  search?: string;
  globalFilterFn?: (row: TData, query: string) => boolean;
  /** `false`: one page */
  pagination?: false | PaginationConfig;
  showTotal?: (summary: GroupedSummary) => ReactNode;
  /** As antd's; `currentDataSource` is the filtered, sorted rows */
  onChange?: TableProps<TData>['onChange'];
}

type Row<TData, TGroup> = TableRow<TData, TGroup>;

const rowClassName = (row: unknown) =>
  isGroupHeaderRow(row) ? styles.groupRow : '';

/**
 * Header rows as one cell across the visible columns, hosted by the first.
 * Column groups (`children`) are not supported.
 */
function withGroupRows<TData, TGroup>(
  columns: TableColumnsType<TData>,
  renderHeader: (row: GroupHeaderRow<TGroup>) => ReactNode,
): TableColumnsType<Row<TData, TGroup>> {
  // antd drops hidden columns before calling `onCell`
  const visibleCount = columns.filter(column => !column.hidden).length;
  const hostIndex = columns.findIndex(column => !column.hidden);

  return columns.map((column, index): TableColumnType<Row<TData, TGroup>> => {
    const { onCell, render } = column as TableColumnType<TData>;
    const isHost = index === hostIndex;
    return {
      ...(column as TableColumnType<Row<TData, TGroup>>),
      onCell: (record, rowIndex) => {
        if (isGroupHeaderRow(record))
          return { colSpan: isHost ? visibleCount : 0 };
        return onCell?.(record as TData, rowIndex) ?? {};
      },
      // rc-table renders a cell even when its `colSpan` is 0
      render: (value, record, rowIndex) => {
        if (!isGroupHeaderRow<TGroup>(record))
          return render ? render(value, record as TData, rowIndex) : value;
        if (!isHost) return null;
        return (
          <div className={styles.groupRowContent}>{renderHeader(record)}</div>
        );
      },
    };
  });
}

/** antd `Table` with group header rows; sorts, filters and pages rows itself. */
export default function GroupedTable<
  TData extends object,
  TGroup extends GroupDef,
>(props: GroupedTableProps<TData, TGroup>) {
  const {
    dataSource,
    columns,
    rowKey,
    grouping,
    groupPaging,
    search,
    globalFilterFn,
    pagination,
    showTotal,
    onChange,
    className,
    ...tableProps
  } = props;
  const pager = pagination === false ? undefined : pagination;

  // Latest `rowKey` through a ref: an inline function mustn't rerun the pipeline
  const rowKeyRef = useRef(rowKey);
  rowKeyRef.current = rowKey;
  const getRowId = useCallback((row: TData) => {
    const key = rowKeyRef.current;
    return typeof key === 'function' ? key(row) : String(row[key]);
  }, []);
  const query = useGroupedTable({
    dataSource,
    columns,
    getRowId,
    grouping,
    groupPaging,
    search,
    globalFilterFn,
    paginate: pagination !== false,
    defaultPageSize: pager?.defaultPageSize,
    onChange,
  });
  const { toggleGroup, onTableChange, onPageChange } = query;
  const renderGroupRow = grouping?.renderGroupRow;

  const renderHeader = useCallback(
    (row: GroupHeaderRow<TGroup>) =>
      renderGroupRow?.({
        groupKey: row.groupKey,
        group: row.group,
        count: row.count,
        collapsed: row.collapsed,
        continued: row.continued,
        toggle: () => toggleGroup(row.groupKey),
      }),
    [renderGroupRow, toggleGroup],
  );
  const tableColumns = useMemo(
    () => withGroupRows(query.columns, renderHeader),
    [query.columns, renderHeader],
  );
  const tableRowKey = useCallback(
    (row: Row<TData, TGroup>) => rowKeyOf(row, getRowId),
    [getRowId],
  );

  const handlePageChange = (page: number, pageSize: number) => {
    onPageChange(page, pageSize);
    pager?.onChange?.(page, pageSize);
  };

  return (
    <div className={className}>
      <Table<Row<TData, TGroup>>
        {...tableProps}
        className={styles.table}
        rowKey={tableRowKey}
        rowClassName={rowClassName}
        columns={tableColumns}
        dataSource={query.rows}
        pagination={false}
        onChange={onTableChange}
      />
      {pagination !== false && query.rows.length > 0 && (
        <Pagination
          size="small"
          align="end"
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          {...pager}
          className={cn(styles.pagination, pager?.className)}
          current={query.page}
          pageSize={query.pageSize}
          total={query.total}
          showTotal={showTotal && (() => showTotal(query.summary))}
          onChange={handlePageChange}
        />
      )}
    </div>
  );
}
